import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { forkJoin, Observable, of } from 'rxjs';

import { AuthService } from '@fry/lib/auth/auth.service';
import { LoggerService, NotifyService } from '@fry/lib/utils';
import { SecurityService } from '@fry/lib/security';
import { catchError, map, mapTo, tap } from 'rxjs/operators';
import { ProfileStore } from '../profile';

const blackListedUrls = [
  '/auth-callback',
  '/logged_out',
];

@Injectable({
  providedIn: 'root'
})
export class AuthGuard  {
  constructor(
    private authService: AuthService,
    private router: Router,
    private security: SecurityService,
    private logger: LoggerService,
    private notify: NotifyService,
    private profile: ProfileStore,
  ) { }

  canActivate(next: ActivatedRouteSnapshot,
  state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {
    if (!this.authService.isLoggedIn) {
      this.logger.info('Not logged in, redirecting to login');
      if (blackListedUrls.indexOf(location.pathname) === -1) {
        localStorage.setItem('lastUrl', location.pathname);
      }
      this.authService.startLogin();
      // this.router.navigate(['/auth/login'], {
      //   queryParams: {
      //     return: state.url
      //   }
      // });
      return false;
    }

    if (next.data && next.data.loginOnly) {
      return true;
    }

    if (!this.authService.organisation) {
      this.logger.info('Not logged in, redirecting to org chooser');
      if (next.routeConfig && next.routeConfig.path === 'set-organisation') {
        return true;
      }
      this.router.navigate(['/auth/set-organisation'], {
        queryParams: {
          return: state.url
        }
      });
      return false;
    }
    let profileObs: Observable<boolean>;
    const obsList: Observable<boolean>[] = [];

    // Verify user is in organisation
    // This can be done either by checking scope or fetching from the server
    profileObs = this.profile.getCurrent().pipe(
      mapTo(true),
      catchError(err => {
        this.logger.warn(err);
        this.router.navigate(['/auth/set-organisation'], {
          queryParams: {
            return: state.url
          }
        });
        return of(false);
      })
    );
    obsList.push(profileObs);

    if (next.data && next.data.requiredPermissions) {
      let obs: Observable<boolean>;
      if (next.data.userParamArg) {
        const username = this.findParamInAncestors(next, next.data.userParamArg);
        if (!username) {
          this.logger.warn('No username, no access');
          obs = of(false);
        } else if (username === 'profile') {
          const permissions = next.data.requiredOwnPermissions !== undefined ?
            next.data.requiredOwnPermissions : `${next.data.requiredPermissions}.own`;
          obs = this.security.hasPermission(permissions);
        } else {
          obs = this.security.hasPotentialPermissionFor(next.data.requiredPermissions, username);
        }
      } else {
        obs = this.security.hasPermission(next.data.requiredPermissions);
      }

      obs = obs.pipe(
        tap(res => {
          if (!res) {
            this.notify.error('You are not authorised to access this content');
            if (this.router.routerState.snapshot.url === '') {
              this.router.navigate([]);
            }
          }
        })
      );

      obsList.push(obs);
    }

    if (next.data && next.data.requiredRole) {
      let obs: Observable<boolean>;
      if (next.data.userParamArg) {
        const username = this.findParamInAncestors(next, next.data.userParamArg);
        if (!username) {
          this.logger.warn('No username, no access');
          obs = of(false);
        } else if (username === 'profile') {
          obs = this.security.hasRole(next.data.requiredRole);
        } else {
          obs = this.security.hasRoleFor(next.data.requiredRole, username);
        }
      } else {
        obs = this.security.hasRole(next.data.requiredRole);
      }

      obs = obs.pipe(
        catchError(err => {
          console.log(err);
          return of(false);
        }),
        tap(res => {
          if (!res) {
            this.notify.error('You are not authorised to access this content');
            if (this.router.routerState.snapshot.url === '') {
              this.router.navigate([]);
            }
          }
        })
      );

      obsList.push(obs);
    }

    if (obsList.length === 0) {
      return of(true);
    }

    return forkJoin(obsList).pipe(
      map(res => res.every(itm => itm)),
    );
  }

  findParamInAncestors(route: ActivatedRouteSnapshot, param: string) {
    let value = route.params[param];
    let lroute = route;
    while (value === undefined && lroute.parent) {
      lroute = lroute.parent;
      value = lroute.params[param];
    }
    return value;
  }
}
